import { useState, useRef, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

type Step = 'email' | 'code';

/**
 * Login lives inside the SPA so the user never leaves trinethub.com.
 * The form talks to api.trinethub.com via CORS (auth-svc allows the apex
 * origin + sends credentials). On success the JWT cookie is set on the
 * api.trinethub.com host, which the browser will still send back on
 * subsequent /api/auth/me calls because trinethub.com and api.trinethub.com
 * are same-site under SameSite=Lax.
 */
export default function Login() {
  const [step, setStep] = useState<Step>('email');
  const [params] = useSearchParams();
  // Landing page can deep-link with ?email=... to pre-fill for a specific role.
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const returnTo = params.get('return_to') || '/';
  const roleHint = params.get('role'); // 'super_admin' | 'tenant_admin' | null

  useEffect(() => {
    if (step === 'code') setTimeout(() => codeRef.current?.focus(), 50);
  }, [step]);

  async function handleEmail(e: FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null);
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setBusy(true);
    try {
      await api.otpRequest(email.trim());
      setInfo(`Check ${email.trim()} for a 6-digit code. It expires in 5 minutes.`);
      setStep('code');
    } catch (e: any) {
      setError(e.message || 'Could not send the code. Please try again.');
    } finally { setBusy(false); }
  }

  async function handleCode(e: FormEvent) {
    e.preventDefault();
    setError(null); setInfo(null);
    if (!/^\d{6}$/.test(code.trim())) { setError('Enter the 6-digit code.'); return; }
    setBusy(true);
    try {
      await api.otpVerify(email.trim(), code.trim());
      setInfo('Signed in. Redirecting…');
      // FULL page reload (not router nav) so App.tsx remounts and re-fetches
      // /api/auth/me with the freshly-set cookie. Otherwise App.tsx's `me`
      // state stays null from the pre-login fetch and the route resolver
      // renders the public Landing — exactly the "code accepted but bounced
      // back" symptom.
      setTimeout(() => { window.location.replace(returnTo); }, 300);
    } catch (e: any) {
      setError(e.message || 'Verification failed.');
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-tnh-bg dark:bg-tnh-bg-dark">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-8">
          <img
            src="/trinethub-logo.png"
            alt="TriNetHub — Advanced Collaboration Platform"
            className="h-24 w-auto dark:bg-white dark:rounded-xl dark:px-4 dark:py-2"
          />
        </div>

        <div className="card p-7">
          <h1 className="text-xl font-semibold">Sign in</h1>
          {roleHint === 'super_admin' && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 font-medium">
              Super-admin · platform owner
            </div>
          )}
          {roleHint === 'tenant_admin' && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium">
              Tenant admin · operator console
            </div>
          )}
          <p className="text-gray-500 text-sm mt-2">
            {step === 'email'
              ? "Enter your email — we'll send a one-time code."
              : `Code sent to ${email}`}
          </p>

          {step === 'email' ? (
            <form onSubmit={handleEmail} className="mt-5 space-y-3" autoComplete="off" noValidate>
              <label htmlFor="email" className="block text-xs text-gray-500">Email</label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 text-base rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent focus:ring-2 focus:ring-tnh-accent/30"
                required
              />
              <button type="submit" disabled={busy} className="btn-primary w-full mt-2">
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleCode} className="mt-5 space-y-3" autoComplete="off" noValidate>
              <label htmlFor="code" className="block text-xs text-gray-500">6-digit code</label>
              <input
                id="code"
                ref={codeRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full px-3.5 py-3 text-2xl font-mono tracking-[0.5em] text-center rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent focus:outline-none focus:border-tnh-accent focus:ring-2 focus:ring-tnh-accent/30"
                required
              />
              <button type="submit" disabled={busy} className="btn-primary w-full mt-2">
                {busy ? 'Verifying…' : 'Verify and sign in'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(null); setInfo(null); }}
                className="btn-ghost w-full"
              >
                Use a different email
              </button>
            </form>
          )}

          {error && <div className="mt-4 text-sm text-tnh-err">{error}</div>}
          {info  && <div className="mt-4 text-sm text-tnh-ok">{info}</div>}
        </div>

        <div className="text-center text-xs text-gray-500 mt-6">
          OTP-only sign-in. No passwords.
        </div>
      </div>
    </div>
  );
}
